/* eslint-disable react/no-unescaped-entities */
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import SEO from '@/components/SEO';

/**
 * Shared chrome for the four legal pages (Mentions légales, Confidentialité,
 * CGU, Contact). Mirrors the on-screen Hérita identity used on HomePage and
 * the Dossier PDF — cream background, navy ink, warm hairlines, Cormorant
 * Garamond display, Work Sans body — so every legal page feels like a
 * native part of the product, not a tacked-on legal page.
 *
 * Public surface:
 *  - eyebrow: short caps label (e.g. "Mentions légales").
 *  - title:   serif H1 of the page.
 *  - lastUpdated: optional ISO/long-form date shown beneath the title.
 *  - children: the actual editorial content (uses .legal-prose styles).
 */
export const LegalPageLayout = ({ eyebrow, title, lastUpdated, testId, children, seoTitle, seoDescription, seoPath }) => {
  return (
    <div
      className="min-h-screen bg-[#F9F7F3] text-[#2C363F]"
      data-testid={testId}
    >
      {seoTitle && (
        <SEO
          title={seoTitle}
          description={seoDescription}
          path={seoPath}
        />
      )}
      {/* Top brand bar — matches the dashboard header */}
      <header className="border-b border-[#E2DBCF] bg-white">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 lg:px-12 py-5 flex items-center justify-between">
          <Link
            to="/"
            className="font-['IBM_Plex_Mono'] text-[11px] sm:text-[12px] uppercase tracking-[0.28em] text-[#1A2530] hover:opacity-80 transition-opacity"
            data-testid="legal-brand-link"
          >
            HÉRITA
          </Link>
          <Link
            to="/"
            className="inline-flex items-center font-['Work_Sans'] text-[12px] sm:text-[13px] text-[#2C363F] hover:text-[#1A2530] transition-colors"
            data-testid="legal-back-home"
          >
            <ArrowLeft className="w-3.5 h-3.5 mr-2" />
            Retour à l'accueil
          </Link>
        </div>
      </header>

      {/* Title block */}
      <section className="px-5 sm:px-8 lg:px-12 pt-14 sm:pt-20 pb-8 sm:pb-10">
        <div className="max-w-3xl mx-auto">
          <p className="font-['IBM_Plex_Mono'] text-[10px] sm:text-[11px] uppercase tracking-[0.3em] text-[#2C363F] opacity-80 mb-4">
            {eyebrow}
          </p>
          <h1
            className="font-['Cormorant_Garamond'] font-light text-[36px] sm:text-[48px] lg:text-[56px] leading-[1.08] text-[#1A2530]"
            style={{ letterSpacing: '-0.015em' }}
            data-testid="legal-page-title"
          >
            {title}
          </h1>
          {lastUpdated && (
            <p className="mt-5 font-['Work_Sans'] text-[12px] sm:text-[13px] text-[#2C363F] italic opacity-80">
              Dernière mise à jour : {lastUpdated}
            </p>
          )}
        </div>
      </section>

      {/* Content */}
      <main className="px-5 sm:px-8 lg:px-12 pb-20 sm:pb-28">
        <article
          className="max-w-3xl mx-auto legal-prose font-['Work_Sans'] text-[15px] sm:text-[15.5px] leading-[1.75] text-[#2C363F]"
          data-testid="legal-page-content"
        >
          {children}
        </article>
      </main>

      {/* Cross-links footer */}
      <footer className="border-t border-[#E2DBCF] bg-white">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 lg:px-12 py-10">
          <p className="font-['Work_Sans'] text-[12px] sm:text-[13px] text-[#2C363F] italic text-center mb-6 leading-relaxed opacity-90 max-w-2xl mx-auto">
            Les analyses sont fournies à titre informatif et ne remplacent pas le conseil
            de votre notaire ou d'un professionnel du droit.
          </p>
          <div className="flex flex-col md:flex-row justify-between items-center pt-6 border-t border-[#E2DBCF]/60 gap-4">
            <p className="font-['Work_Sans'] text-sm text-[#2C363F]">
              © 2026 Hérita. Tous droits réservés.
            </p>
            <div className="flex flex-wrap gap-x-6 gap-y-2 justify-center">
              <Link
                to="/mentions-legales"
                className="font-['Work_Sans'] text-sm text-[#2C363F] hover:text-[#1A2530] transition-colors duration-300"
                data-testid="legal-link-mentions"
              >
                Mentions légales
              </Link>
              <Link
                to="/confidentialite"
                className="font-['Work_Sans'] text-sm text-[#2C363F] hover:text-[#1A2530] transition-colors duration-300"
                data-testid="legal-link-confidentialite"
              >
                Confidentialité
              </Link>
              <Link
                to="/cgu"
                className="font-['Work_Sans'] text-sm text-[#2C363F] hover:text-[#1A2530] transition-colors duration-300"
                data-testid="legal-link-cgu"
              >
                CGU
              </Link>
              <Link
                to="/contact"
                className="font-['Work_Sans'] text-sm text-[#2C363F] hover:text-[#1A2530] transition-colors duration-300"
                data-testid="legal-link-contact"
              >
                Contact
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LegalPageLayout;
